// src/AdminEssays.js
import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from './AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import './styles/AdminEssays.scss';
import logger from './utils/logger';
import { track } from '@vercel/analytics';

function AdminEssays({ authToken, onContinueEssay }) {
  const [records, setRecords] = useState([]); // Initialize as empty array
  const [error, setError] = useState(null);
  const [expandedDates, setExpandedDates] = useState(new Set());
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { clearToken } = useContext(AuthContext);
  const navigate = useNavigate();
  // State for filters
  const [filters, setFilters] = useState({
    hasTeacherFeedback: false,
    hasModifiedFeedback: false,
    endsWithTeacher: false,
  });

  // Handle filter changes - trigger refetch
  const handleFilterChange = (event) => {
    const { name, checked } = event.target;
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: checked,
    }));
    // Reset page and clear records when filters change
    setCurrentPage(1);
    setRecords([]); 
    setHasMore(true); // Assume there might be more pages with new filters
    // Refetch will be triggered by the useEffect below
  };

  const fetchAdminEssays = async (page, currentFilters) => {
    if (!authToken) {
      logger.info('No auth token found');
      setError('No authentication token found');
      // navigate('/'); // Avoid navigation if already logged out
      return;
    }

    setIsLoading(true);
    try {
      logger.info(`Fetching admin essays - Page: ${page}, Filters:`, currentFilters);

      // Build query parameters from filter state
      const queryParams = new URLSearchParams();
      if (currentFilters.hasTeacherFeedback) queryParams.append('has_teacher_feedback', 'true');
      if (currentFilters.hasModifiedFeedback) queryParams.append('has_modified_feedback', 'true');
      if (currentFilters.endsWithTeacher) queryParams.append('ends_with_teacher', 'true');
      const queryString = queryParams.toString();
      const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/admin/essays/${page}${queryString ? '?' + queryString : ''}`;

      logger.debug('API URL with filters:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      const responseText = await response.text();
      logger.info('Admin essays response:', {
        status: response.status,
        text: responseText
      });

      if (!response.ok) {
        // Handle specific errors like 401/403 if needed
        if (response.status === 401 || response.status === 403) {
          clearToken(); // Clear token on auth error
          navigate('/login');
          throw new Error('Authentication failed. Please log in again.');
        }
        throw new Error(`Server error: ${responseText}`);
      }

      try {
        const data = JSON.parse(responseText);
        logger.info('Parsed admin essays data:', data);
        
        if (data.essays) {
          setRecords(prevRecords => 
            page === 1 ? data.essays : [...prevRecords, ...data.essays] // Keep existing logic for pagination append
          );
          setHasMore(data.has_more);
          logger.info('Records set:', data.essays.length, 'items');
        } else {
          logger.error('Received invalid data format:', data);
          setError('Invalid data format received from server');
        }
      } catch (parseError) {
        logger.error('JSON Parse error:', parseError);
        logger.error('Response text that failed to parse:', responseText);
        throw new Error('Invalid response format from server');
      }
    } catch (err) {
      logger.error('Admin essays fetch error:', {
        message: err.message,
        stack: err.stack,
        cause: err.cause
      });
      setError(err.message);
      // Optional: Set records to empty array on error?
      // setRecords([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Trigger fetch when page or filters change
  useEffect(() => {
    fetchAdminEssays(currentPage, filters);
  }, [authToken, navigate, currentPage, filters]); // Add filters to dependency array

  // Add debug logging for records state changes
  useEffect(() => {
    logger.info('Records state updated:', records);
  }, [records]);

  // Group records by date using the raw records from state (now pre-filtered by backend)
  const groupedRecords = {};
  if (Array.isArray(records)) {
    records.forEach(record => { // Use 'records' directly
      const date = new Date(record.created_at).toLocaleDateString();
      if (!groupedRecords[date]) {
        groupedRecords[date] = [];
      }
      groupedRecords[date].push(record);
    });
  }

  const toggleDate = (date) => {
    const newExpandedDates = new Set(expandedDates);
    if (newExpandedDates.has(date)) {
      newExpandedDates.delete(date);
    } else {
      newExpandedDates.add(date);
    }
    setExpandedDates(newExpandedDates);
  };

  const toggleSection = (recordId, section) => {
    const sectionKey = `${recordId}-${section}`;
    const newExpandedSections = new Set(expandedSections);
    if (newExpandedSections.has(sectionKey)) {
      newExpandedSections.delete(sectionKey);
    } else {
      newExpandedSections.add(sectionKey);
    }
    setExpandedSections(newExpandedSections);
  };

  const isSectionExpanded = (recordId, section) => {
    return expandedSections.has(`${recordId}-${section}`);
  };

  const handleViewFeedback = (essay) => {
    logger.info('handleViewFeedback called with essay:', essay);
    
    // Track viewing feedback
    track('admin_view_feedback', {
      category: essay.category || 'Shakespeare',
      is_math: essay.category === 'MATH_Concepts' || essay.category === 'MATH_Applications',
      user_role: 'admin'
    });
    
    // Check if this is a math category and ensure category is properly set
    const isMathCategory = essay.category === 'MATH_Concepts' || essay.category === 'MATH_Applications';
    logger.info('Is math category detected:', isMathCategory, 'Category:', essay.category);
    
    // Make sure the category is explicitly set to the math category value
    const mathCategory = isMathCategory ? essay.category : null;
    
    const continueEssay = {
      question: essay.question_text,
      answer: essay.answer_text,
      result: essay.feedback,
      category: mathCategory || essay.category || 'Shakespeare', // Ensure category is properly preserved
      startAtStep: isMathCategory ? 2 : 5, // Go directly to step 2 for math, 5 for others
      skipGrading: true,
      task_id: essay.task_id,
      essayId: essay.id,
      teacherFeedback: essay.teacher_feedback,
      isMathFeedback: isMathCategory // Flag to indicate this is math feedback
    };
    logger.info('Saving to localStorage for view feedback:', continueEssay);
    
    // Save to localStorage
    localStorage.setItem('continueEssay', JSON.stringify(continueEssay));
    
    // Call onContinueEssay with the essay data directly
    onContinueEssay(continueEssay);
  };

  return (
    <div className="dashboard-container" style={{ 
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto'
    }}>
      {/* Navigation Bar */}
      <nav style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
        padding: '10px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px'
      }}>
        <h1 style={{ margin: 0 }}>Admin Dashboard</h1>
        <div>
          <Link 
            to="/"
            style={{
              marginRight: '15px',
              textDecoration: 'none',
              color: '#0066cc',
              padding: '8px 16px',
              borderRadius: '4px',
              border: '1px solid #0066cc'
            }}
          >
            Back to Main
          </Link>
          <button
            onClick={() => {
              window.isManualLogout = true;
              clearToken();
              navigate('/');
            }}
            style={{
              padding: '8px 16px',
              borderRadius: '4px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Logout
          </button>
        </div>
      </nav>

      {/* Filter Controls */}
      <div className="filter-controls" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#eee', borderRadius: '5px' }}>
        <h4>Filter Essays:</h4>
        <label style={{ marginRight: '15px' }}>
          <input
            type="checkbox"
            name="hasTeacherFeedback"
            checked={filters.hasTeacherFeedback}
            onChange={handleFilterChange}
          />
          Used Teacher Feedback Form
        </label>
        <label style={{ marginRight: '15px' }}>
          <input
            type="checkbox"
            name="hasModifiedFeedback"
            checked={filters.hasModifiedFeedback}
            onChange={handleFilterChange}
          />
          Any Teacher Edits of AI Feedback
        </label>
        <label>
          <input
            type="checkbox"
            name="endsWithTeacher"
            checked={filters.endsWithTeacher}
            onChange={handleFilterChange}
          />
          Teacher Grade Edits
        </label>
      </div>

      {/* Error Display */}
      {error && (
        <div style={{ 
          color: 'red',
          padding: '10px',
          marginBottom: '20px',
          backgroundColor: '#ffebee',
          borderRadius: '4px'
        }}>
          {error}
        </div>
      )}

      {/* Records Display */}
      <div className="records-container">
        {!isLoading && (!records || records.length === 0) ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            marginTop: '20px'
          }}>
            <p style={{ fontSize: '1.2em', color: '#666' }}>
              {error ? `Error: ${error}` : 'No records found.'}
            </p>
            <Link 
              to="/"
              style={{
                display: 'inline-block',
                marginTop: '15px',
                padding: '10px 20px',
                backgroundColor: '#0066cc',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '4px'
              }}
            >
              Start New Analysis
            </Link>
          </div>
        ) : (
          Object.entries(groupedRecords) // Use groupedRecords derived from raw 'records'
            .sort((a, b) => new Date(b[0]) - new Date(a[0]))
            .map(([date, dateRecords]) => (
              <div key={date} className="essay-section">
                <div
                  onClick={() => toggleDate(date)}
                  className="date-header"
                >
                  <h2>
                    {date} ({dateRecords.length} {dateRecords.length === 1 ? 'entry' : 'entries'})
                  </h2>
                  <span>{expandedDates.has(date) ? '▼' : '▶'}</span>
                </div>

                {expandedDates.has(date) && (
                  <div className="essay-date-section">
                    {dateRecords.map((record) => (
                      <div key={record.id} className="essay-card">
                        <div className="essay-info">
                          <strong>Student:</strong> {record.username}
                          <span style={{ marginLeft: '15px' }}><strong>ID:</strong> {record.id}</span>
                        </div>
                        
                        {/* Question section */}
                        <div
                          onClick={() => toggleSection(record.id, 'question')}
                          className="collapsible-header"
                        >
                          <strong>Question</strong>
                          <span>{isSectionExpanded(record.id, 'question') ? '▼' : '▶'}</span>
                        </div>
                        {isSectionExpanded(record.id, 'question') && (
                          <p className="collapsible-content">{record.question_text}</p>
                        )}

                        {/* Answer section */}
                        <div
                          onClick={() => toggleSection(record.id, 'answer')}
                          className="collapsible-header"
                        >
                          <strong>Answer</strong>
                          <span>{isSectionExpanded(record.id, 'answer') ? '▼' : '▶'}</span>
                        </div>
                        {isSectionExpanded(record.id, 'answer') && (
                          <p className="collapsible-content">{record.answer_text}</p>
                        )}

                        {/* Feedback section */}
                        <div
                          onClick={() => toggleSection(record.id, 'feedback')}
                          className="collapsible-header"
                        >
                          <strong>Feedback</strong>
                          <span>{isSectionExpanded(record.id, 'feedback') ? '▼' : '▶'}</span>
                        </div>
                        {isSectionExpanded(record.id, 'feedback') && (
                          <p className="collapsible-content">{record.feedback}</p>
                        )}

                        <div className="essay-footer">
                          <div><strong>Category:</strong> {record.category}</div>
                          <div className="status">
                            <strong>Status:</strong> {record.feedback ? 'Graded' : 'Draft'}
                          </div>
                          <div className="usage-indicator">
                            Submitted: {new Date(record.created_at).toLocaleDateString()}
                          </div>
                          {record.feedback && (
                            <button
                              onClick={() => handleViewFeedback(record)}
                              className="view-feedback-button"
                            >
                              View Feedback
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
        )}
      </div>

      {/* Load More Button */}
      {hasMore && (
        <div style={{ textAlign: 'center', marginTop: '20px', marginBottom: '40px' }}>
          <button
            onClick={() => {
              const nextPage = currentPage + 1;
              setCurrentPage(nextPage);
              // fetchAdminEssays will be called by the useEffect hook
            }}
            disabled={isLoading}
            style={{
              padding: '10px 20px',
              backgroundColor: '#2980b9',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1
            }}
          >
            {isLoading ? 'Loading...' : 'Load More Essays'}
          </button>
        </div>
      )}
    </div>
  );
}

export default AdminEssays;